import { signalStore, withComputed, withMethods } from '@ngrx/signals';
import { LottoDetails } from './models/lotto.model';
import { computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BalanceQuery } from '../../../account/balance/balance.query';
import { config } from '../../../shared/config';
import { LOTTO_STATE } from '../models/lotto-item.model';
import { DateTime } from 'luxon';
import { LottoUIStatus } from './models/lotto-ui-status.model';
import { LoyaltyLimitType } from '../models/loyalty-limit-type';
import { withEntityState } from '../../../shared/store/entity-state.feature';
import { REWARD_STATUS } from '../../shared/models/reward-status.enum';

/**
 * Resolves the display status of a lotto item based on its current state and the user's balance.
 * Returns a `LottoUIStatus` object containing the theme, status text, and whether the item is disabled.
 
 */
function resolveLottoStatus(item: LottoDetails, balance: Signal<number | undefined>): LottoUIStatus {
  const requestTime = DateTime.fromISO(item.RequestTime as string, { zone: 'utc' });
  const startDate = DateTime.fromISO(item?.StartDate as string, { zone: 'utc' });
  let comingSoon = item.Status === REWARD_STATUS.ComingSoon;

  if (startDate <= requestTime) {
    comingSoon = false;
  }
  if (item.Status === REWARD_STATUS.ViewableOnly) {
    return { theme: 'item-locked', statusText: LOTTO_STATE.LOCKED, disabled: true };
  } else if (comingSoon && startDate > requestTime) {
    return { theme: 'coming-soon', statusText: LOTTO_STATE.COMING_SOON, disabled: true };
  } else if (item.Status === REWARD_STATUS.SoldOut) {
    return { theme: 'sold-out', statusText: LOTTO_STATE.SOLD_OUT, disabled: true };
  } else if (
    item.Status === REWARD_STATUS.LimitReached ||
    (item.NumberOfTicketsPurchasedByAccount >= item.LimitCount && item.LimitType !== LoyaltyLimitType.NoLimit)
  ) {
    return { theme: 'limit-reached', statusText: LOTTO_STATE.LIMIT_REACHED, disabled: true };
  } else {
    if ((balance() ?? 0) < item.PurchasePrice) {
      return { theme: 'not-enough-points', statusText: LOTTO_STATE.NOT_ENOUGH_POINT, disabled: true };
    } else if (item.Status === REWARD_STATUS.Favourite) {
      return { theme: 'featured', statusText: LOTTO_STATE.FEATURED, disabled: false };
    } else if (item.Status === REWARD_STATUS.NewItem) {
      return { theme: 'new', statusText: LOTTO_STATE.NEW, disabled: false };
    } else if (item.Status === REWARD_STATUS.DisabledManualEntry) {
      return { theme: undefined, statusText: LOTTO_STATE.VIEW, disabled: true };
    } else {
      return { theme: undefined, statusText: '', disabled: false };
    }
  }
}

/**
 * Lotto Store version 2.0 using ngrx signal store
 */
export const LottoItemStore = signalStore(
  { providedIn: 'root' },
  withEntityState<LottoDetails, 'LottoId'>('LottoId', { sortBy: 'EndDate' }),
  withComputed(({ entities }, balanceQuery = inject(BalanceQuery)) => {
    const balance = toSignal(balanceQuery.selectPurse(config.purseConfig.virtualLoyalty), { initialValue: 0 });
    return {
      uiStatusMap: computed(() => new Map(entities().map((lotto) => [lotto.LottoId, resolveLottoStatus(lotto, balance)]))),
    };
  }),
  withMethods((store) => ({
    /* ================================
     * Getters
     * ================================ */

    /** Returns a computed signal for the resolved UI status of a lotto by id. */
    getStatusById(id: number) {
      return computed(() => store.uiStatusMap().get(id));
    },
  }))
);
